import { useState } from "react";
import { useDispatch } from "react-redux";
import { toast } from "react-toastify";
import CustomerPageLayout from "../../customer-page-layout/CustomerPageLayout";
import { CardFrame } from "../../../../common/components/card-frame/CardFrame";
import UnlinkMeterRow from "../components/unlink-meter-row/UnlinkMeterRow";
import UnlinkConfirmModal from "../components/unlink-confirm-modal/UnlinkConfirmModal";
import MeterDetailsModal from "../components/meter-details-modal/MeterDetailsModal";
import { useGetCustomerMeterNumbersQuery, useUnlinkCustomerMeterMutation } from "../../../../service/vending-service";
import { ConsumerType, CustomerMeterNumber } from "../../../../model/energy-profile";
import { clearEnergyProfileInfo } from "../../../../stores/slices/customer-energy-profile-slice";
import { getErrorMessage } from "../../../../utils/error-handler";
import Can from "../../../../guards/Can";
import { CustomerPermissions } from "../../../../model/permissions/permissions";
import styles from "./manage-meters.module.scss";

const meterOrAccountNumber = (meter: CustomerMeterNumber): string => {
	if (meter.consumerType === ConsumerType.Postpaid) return meter.accountNumber;
	if (meter.consumerType === ConsumerType.Prepaid) return meter.meterNumber;
	return "";
};

export const ManageMetersPage = () => {
	const dispatch = useDispatch();
	const { data: meters = [], isLoading } = useGetCustomerMeterNumbersQuery();
	const [unlinkCustomerMeter, { isLoading: isUnlinking }] = useUnlinkCustomerMeterMutation();
	const [selectedMeterId, setSelectedMeterId] = useState<string | null>(null);
	const [detailsMeter, setDetailsMeter] = useState<CustomerMeterNumber | null>(null);

	const handleConfirmUnlink = async () => {
		if (!selectedMeterId) return;
		const isLastMeter = meters.length <= 1;
		try {
			await unlinkCustomerMeter(selectedMeterId).unwrap();
			toast.success("Meter unlinked successfully");
			setSelectedMeterId(null);
			if (isLastMeter) {
				dispatch(clearEnergyProfileInfo());
			}
		} catch (error: any) {
			toast.error(getErrorMessage(error?.data ?? error));
		}
	};

	return (
		<CustomerPageLayout title="Manage Meters" showBackButton>
			<div className={styles.cardWrapper}>
				<CardFrame headerSectionTitle="Choose meter">
					{isLoading ? (
						<div className={styles.meterList}>
							{Array.from({ length: 3 }).map((_, i) => (
								<div className={`${styles.skeletonRow} ${i === 2 ? styles.last : ""}`} key={i}>
									<span className={styles.skeletonAvatar} />
									<div className={styles.skeletonInfo}>
										<span className={styles.skeletonLine} />
										<span className={`${styles.skeletonLine} ${styles.skeletonLineShort}`} />
									</div>
								</div>
							))}
						</div>
					) : (
						<Can permission={CustomerPermissions.ViewMeters}>
								<div className={styles.meterList}>
									{meters.map((meter, index) => (
										<UnlinkMeterRow
											key={meter.id}
											index={index}
											name={meter.name}
											number={meterOrAccountNumber(meter)}
											isLast={index === meters.length - 1}
											onClick={() => setDetailsMeter(meter)}
											onUnlink={() => setSelectedMeterId(meter.id)}
										/>
									))}
								</div>
						</Can>
					)}
				</CardFrame>
			</div>

			<MeterDetailsModal isOpen={detailsMeter !== null} meter={detailsMeter} onClose={() => setDetailsMeter(null)} />

			<UnlinkConfirmModal
				isOpen={selectedMeterId !== null}
				onClose={() => setSelectedMeterId(null)}
				onConfirm={handleConfirmUnlink}
				isLoading={isUnlinking}
			/>
		</CustomerPageLayout>
	);
};

export default ManageMetersPage;
